import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, LinearProgress, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert, CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { httpGet, httpPost, httpPut } from '../lib/http';

export default function DailyStudyGoals({ userId }) {
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetMinutes, setTargetMinutes] = useState(60);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadGoals();
  }, [userId]);

  async function loadGoals() {
    if (!userId) return;

    setLoading(true);
    try {
      const res = await httpGet(`/api/users/${encodeURIComponent(userId)}/daily-goals`);
      if (res && res.ok) {
        const data = await res.json();
        setGoals(data.goals || data);
        if (data.targetMinutes) {
          setTargetMinutes(data.targetMinutes);
        }
      } else {
        // Initialize default goals
        setGoals({
          targetMinutes: 60,
          todayMinutes: 0,
          tasksCompleted: 0,
          targetTasks: 3,
          streak: 0
        });
      }
    } catch (err) {
      console.error('Error loading daily goals:', err);
      setGoals({
        targetMinutes: 60,
        todayMinutes: 0,
        tasksCompleted: 0,
        targetTasks: 3,
        streak: 0
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateGoals() {
    if (!userId || saving) return;

    setSaving(true);
    try {
      const payload = {
        targetMinutes: parseInt(targetMinutes) || 60,
        targetTasks: goals.targetTasks || 3
      };

      const res = await httpPut(
        `/api/users/${encodeURIComponent(userId)}/daily-goals`,
        payload
      );

      if (res && res.ok) {
        const data = await res.json();
        setGoals(data.goals || data);
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Error updating goals:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !goals) {
    return (
      <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading goals...</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const progressPercent = Math.min((goals.todayMinutes / goals.targetMinutes) * 100, 100);
  const tasksProgressPercent = Math.min((goals.tasksCompleted / goals.targetTasks) * 100, 100);
  const isGoalMet = goals.todayMinutes >= goals.targetMinutes && goals.tasksCompleted >= goals.targetTasks;

  return (
    <>
      <Card sx={{ 
        borderRadius: '16px', 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: isGoalMet ? 'success.main' : 'divider'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700}>
                  🎯 Daily Goals
                </Typography>
                {isGoalMet && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />}
              </Stack>
              <Button
                startIcon={<EditIcon />}
                size="small"
                onClick={() => setDialogOpen(true)}
                sx={{ 
                  borderRadius: '16px',
                  fontWeight: 600
                }}
              >
                Edit
              </Button>
            </Box>

            {isGoalMet && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: '16px' }}>
                <Typography variant="body2" fontWeight={600}>
                  🎉 Amazing! You've crushed today's goal!
                </Typography>
              </Alert>
            )}

            {/* Time Goal */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  ⏱️ Study Time
                </Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">
                  {goals.todayMinutes} / {goals.targetMinutes} mins
                </Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={progressPercent}
                sx={{ 
                  height: 10, 
                  borderRadius: '16px',
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '16px',
                    background: progressPercent === 100 
                      ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                      : 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
                  }
                }}
              />
            </Box>

            {/* Tasks Goal */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  ✅ Topics Completed
                </Typography>
                <Typography variant="body1" fontWeight={700} color="secondary.main">
                  {goals.tasksCompleted} / {goals.targetTasks}
                </Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={tasksProgressPercent}
                sx={{ 
                  height: 10, 
                  borderRadius: '16px',
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '16px',
                    background: tasksProgressPercent === 100 
                      ? 'linear-gradient(90deg, #9c27b0 0%, #ba68c8 100%)'
                      : 'linear-gradient(90deg, #f50057 0%, #ff4081 100%)'
                  }
                }}
                color="secondary"
              />
            </Box>

            {/* Streak */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 2,
              bgcolor: goals.streak >= 3 ? 'warning.light' : 'action.hover',
              borderRadius: '16px'
            }}>
              <Typography variant="body2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🔥 Current Streak
              </Typography>
              <Chip 
                label={`${goals.streak} ${goals.streak === 1 ? 'day' : 'days'}`}
                color={goals.streak >= 7 ? 'success' : goals.streak >= 3 ? 'warning' : 'default'}
                size="medium"
                sx={{ 
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  height: 32
                }}
              />
            </Box>

            {/* Motivational Message */}
            {!isGoalMet && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'primary.light',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.dark' }}>
                  {progressPercent >= 75 ? '🔥 Almost there! Keep going!' :
                   progressPercent >= 50 ? '💪 You\'re halfway there!' :
                   progressPercent >= 25 ? '✨ Great start! Keep it up!' :
                   '📚 Time to study! Let\'s begin!'}
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Edit Daily Goals</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Target Study Time (minutes)"
              type="number"
              value={targetMinutes}
              onChange={(e) => setTargetMinutes(e.target.value)}
              helperText="Set your daily study time goal in minutes"
              inputProps={{ min: 15, max: 480 }}
            />
            <Typography variant="caption" color="text.secondary">
              💡 Tip: Start with a realistic goal and increase gradually. Consistency is more important than duration!
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateGoals} 
            disabled={saving || !targetMinutes}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
